import React, { useEffect, useState, useRef } from "react";
import { useParams, Link } from "react-router-dom";
import {
  MapPin, Star, Tent, Flame, Camera, Wifi, Dog, Mountain,
  ArrowRight, Download, Copy, ExternalLink, Loader2,
} from "lucide-react";
import { api } from "../lib/api";
import { toast } from "sonner";

/**
 * iter121 — Universal campsite promo reel.
 *
 * /share/promo/:campsiteId renders three 9:16 scenes that any campsite can
 * be screen-recorded into. The reel pulls live data (`name`, `region`,
 * `image`, `rating`, `description`, `pitch_types`) so swapping the cover
 * photo in Mongo auto-updates the reel — no redeploy needed.
 *
 * Used by the Print Pack for our 5 "most-clicked" promo reels.
 */

const PUBLIC_URL = (process.env.REACT_APP_PUBLIC_URL || "https://camp-compass.uk").replace(/\/$/, "");

function Sticker({ children, color = "#fff", textColor = "#0e0e0e" }) {
  return (
    <span
      className="inline-flex items-center gap-1.5 px-2.5 py-1 text-[10px] font-bold uppercase tracking-[0.22em] mono"
      style={{ background: color, color: textColor }}
    >
      {children}
    </span>
  );
}

function Scene({ children, bg, testid, label }) {
  return (
    <div className="flex flex-col items-center">
      <div className="mb-2 text-[11px] mono uppercase tracking-[0.25em] text-[#888]">{label}</div>
      <section
        className="relative w-[270px] sm:w-[300px] overflow-hidden shadow-2xl text-white"
        style={{ aspectRatio: "9 / 16", background: bg }}
        data-testid={testid}
      >
        {children}
        <div
          className="absolute bottom-0 left-0 right-0 px-2 py-1 bg-black/65 backdrop-blur-sm text-white text-[10px] sm:text-[11px] font-bold mono uppercase tracking-[0.22em] text-center pointer-events-none z-10"
          aria-hidden="true"
        >
          🌐 camp-compass.uk
        </div>
      </section>
    </div>
  );
}

function CopyBlock({ title, body, testid }) {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(body);
      setCopied(true);
      toast.success("Caption copied");
      setTimeout(() => setCopied(false), 1500);
    } catch { toast.error("Couldn't copy — long-press to copy manually"); }
  };
  return (
    <div className="bg-[#0e0e0e] text-white border border-[#1f1f1f] p-4" data-testid={testid}>
      <div className="flex items-center justify-between gap-3 mb-2">
        <div className="font-display text-sm">{title}</div>
        <button
          onClick={copy}
          className="text-xs mono uppercase tracking-wider text-[#ff4500] hover:underline inline-flex items-center gap-1"
        >
          <Copy className="w-3.5 h-3.5" /> {copied ? "Copied" : "Copy"}
        </button>
      </div>
      <pre className="whitespace-pre-wrap font-sans text-xs sm:text-sm text-[#ddd] leading-relaxed">{body}</pre>
    </div>
  );
}

export default function CampsitePromoReel() {
  const { campsiteId } = useParams();
  const [site, setSite] = useState(null);
  const [error, setError] = useState(null);
  const reelRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get(`/campsites/${campsiteId}`);
        if (!cancelled) {
          setSite(data);
          document.title = `${data.name} · Reel · CampCompass UK`;
        }
      } catch (e) {
        if (!cancelled) setError("Couldn't load this campsite.");
      }
    })();
    return () => { cancelled = true; };
  }, [campsiteId]);

  if (error) {
    return (
      <div className="min-h-screen bg-[#0e0e0e] text-white flex items-center justify-center p-6">
        <div className="text-center">
          <div className="font-display text-2xl mb-2">{error}</div>
          <Link to="/" className="text-[#ff4500] underline">Back to landing</Link>
        </div>
      </div>
    );
  }
  if (!site) {
    return (
      <div className="min-h-screen bg-[#0e0e0e] text-white flex items-center justify-center">
        <Loader2 className="w-6 h-6 animate-spin text-[#ff4500]" />
      </div>
    );
  }

  const listingUrl = `${PUBLIC_URL}/campsites/${site.campsite_id}`;
  const hashtagRegion = (site.region || "").split(",")[0].toLowerCase().replace(/\s+/g, "");
  const captionLong = `🏕 ${site.name} · ${site.region || "UK"}
${typeof site.rating === "number" && site.rating > 0 ? `⭐ ${site.rating.toFixed(1)} rating\n` : ""}
${(site.description || "").split(".").slice(0, 2).join(".")}.

📍 See the full listing, photos, and what's nearby:
${listingUrl}

Plus 22,000+ other UK campsites at camp-compass.uk

#ukcamping #campingweekend ${hashtagRegion ? `#${hashtagRegion}` : ""} #campingtrip`;

  const captionShort = `📍 ${site.name}, ${(site.region || "UK").split(",")[0]} 🏕${typeof site.rating === "number" && site.rating > 0 ? `\n⭐ ${site.rating.toFixed(1)}` : ""}

See it on camp-compass.uk:
${listingUrl}`;

  return (
    <div className="min-h-screen bg-[#0e0e0e] text-white pb-24" data-testid={`promo-reel-${site.campsite_id}`}>

      {/* Top bar */}
      <div className="max-w-5xl mx-auto px-4 sm:px-6 pt-8 pb-6">
        <Link to="/" className="text-xs mono text-[#888] hover:text-[#ff4500] uppercase tracking-wider inline-flex items-center gap-1">
          ← Back
        </Link>
        <div className="mt-3 inline-flex items-center gap-2 text-[11px] mono uppercase tracking-[0.25em] text-[#ff4500]">
          <Star className="w-3.5 h-3.5" fill="currentColor" /> Trending site reel
        </div>
        <h1 className="font-display text-3xl sm:text-4xl mt-2">{site.name}</h1>
        <p className="text-sm text-[#bbb] mt-2 max-w-xl">
          Three vertical 9:16 scenes — screen-record one or all and post to
          Instagram/TikTok/Facebook. The footer ribbon on every scene burns
          <span className="text-[#ff4500] font-bold"> camp-compass.uk </span>
          into the frame so viewers know where to go.
        </p>
      </div>

      {/* THREE SCENES */}
      <div ref={reelRef} className="max-w-5xl mx-auto px-4 sm:px-6">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 justify-items-center">
          {/* SCENE 1 — HERO */}
          <Scene label="Scene 1 · hero" testid="promo-scene-1" bg="#000">
            {site.image && (
              <img
                src={site.image}
                alt={site.name}
                className="absolute inset-0 w-full h-full object-cover opacity-90"
              />
            )}
            <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-transparent to-black/85" />
            <div className="absolute top-5 left-5 right-5 flex items-start justify-between">
              <Sticker color="#ff4500" textColor="#ffffff">
                <Flame className="w-3 h-3" /> Trending
              </Sticker>
              {typeof site.rating === "number" && site.rating > 0 && (
                <Sticker color="#fde047">
                  <Star className="w-3 h-3" fill="#0e0e0e" /> {site.rating.toFixed(1)}
                </Sticker>
              )}
            </div>
            <div className="absolute bottom-10 left-5 right-5 z-[1]">
              <Sticker color="#ffffff">
                <MapPin className="w-3 h-3" /> {(site.region || "UK").split(",")[0]}
              </Sticker>
              <div className="font-display text-[28px] leading-[1.05] mt-3 drop-shadow-[0_3px_12px_rgba(0,0,0,0.8)]">
                {site.name}
              </div>
              <div className="text-[11px] mono uppercase tracking-[0.25em] text-[#ff4500] mt-3">
                Tap to see it on camp-compass.uk
              </div>
            </div>
          </Scene>

          {/* SCENE 2 — DESCRIPTION + STAT STACK */}
          <Scene
            label="Scene 2 · what's on site"
            testid="promo-scene-2"
            bg="linear-gradient(180deg, #ff4500 0%, #b73200 60%, #0e0e0e 100%)"
          >
            <div className="p-5 pt-7 text-center">
              <Sticker color="#ffffff" textColor="#ff4500">
                <Tent className="w-3 h-3" /> What's on site
              </Sticker>
            </div>
            <div className="px-5 text-center space-y-3">
              <div className="font-display text-2xl leading-tight drop-shadow-[0_3px_12px_rgba(0,0,0,0.5)]">
                {site.name}
              </div>
              {site.pitch_types && site.pitch_types.length > 0 && (
                <div className="flex flex-wrap justify-center gap-1.5">
                  {site.pitch_types.slice(0, 4).map((p, i) => (
                    <span key={i} className="text-[9px] mono uppercase tracking-wider bg-white/15 px-2 py-1 backdrop-blur-sm">
                      {p}
                    </span>
                  ))}
                </div>
              )}
              <div className="text-[12px] leading-snug text-white/95 mt-3">
                {(site.description || "Hand-picked by the CampCompass community.").slice(0, 220)}
                {(site.description || "").length > 220 ? "…" : ""}
              </div>
            </div>
            <div className="absolute bottom-12 left-5 right-5">
              <div className="grid grid-cols-3 gap-1 text-center">
                {[
                  { Icon: Mountain, label: "Region", value: (site.region || "UK").split(",")[0] },
                  { Icon: Star, label: "Rating", value: typeof site.rating === "number" && site.rating > 0 ? site.rating.toFixed(1) : "—" },
                  { Icon: Tent, label: "Pitches", value: (site.pitch_types?.length || "Mixed").toString() },
                ].map(({ Icon, label, value }, i) => (
                  <div key={i} className="bg-black/40 backdrop-blur-sm px-1.5 py-2 text-white">
                    <Icon className="w-3.5 h-3.5 mx-auto mb-1 text-[#ffe7d6]" />
                    <div className="font-display text-sm leading-none">{value}</div>
                    <div className="text-[8px] mono uppercase tracking-wider opacity-70 mt-1">{label}</div>
                  </div>
                ))}
              </div>
            </div>
          </Scene>

          {/* SCENE 3 — CTA */}
          <Scene
            label="Scene 3 · tap to see it"
            testid="promo-scene-3"
            bg="linear-gradient(180deg, #0e0e0e 0%, #1a1a1a 50%, #ff4500 100%)"
          >
            <div className="p-5 pt-7 text-center">
              <Sticker color="#ff4500" textColor="#ffffff">
                <Camera className="w-3 h-3" /> See the listing
              </Sticker>
            </div>
            <div className="absolute inset-0 flex flex-col items-center justify-center px-5 text-center">
              <div className="font-display text-[42px] leading-[0.95] drop-shadow-[0_3px_12px_rgba(0,0,0,0.6)]">
                Tap.<br />Look.<br />Book.
              </div>
              <div className="mt-6 bg-white text-[#0e0e0e] inline-block px-4 py-3">
                <div className="font-display text-base leading-tight">{site.name}</div>
                <div className="mono text-[10px] uppercase tracking-wider mt-1 opacity-70">
                  camp-compass.uk
                </div>
              </div>
              <div className="mt-6 text-[11px] mono uppercase tracking-[0.22em] text-white/95">
                + 22,000 other UK sites
              </div>
            </div>
          </Scene>
        </div>
      </div>

      {/* CAPTIONS + LISTING LINK */}
      <div className="max-w-5xl mx-auto px-4 sm:px-6 mt-12 space-y-4">
        <div className="border border-[#1f1f1f] bg-[#0e0e0e] p-4 flex items-center justify-between gap-3 flex-wrap">
          <div className="min-w-0">
            <div className="text-xs mono uppercase tracking-wider text-[#888]">Listing URL · tap to copy</div>
            <div className="font-mono text-sm text-[#ff4500] break-all">{listingUrl}</div>
          </div>
          <div className="flex items-center gap-2">
            <a
              href={listingUrl}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-1 px-3 py-2 bg-white text-[#0e0e0e] font-bold text-xs uppercase tracking-wider mono hover:bg-[#ffe7d6]"
              data-testid="promo-open-listing"
            >
              <ExternalLink className="w-3.5 h-3.5" /> Open
            </a>
            <button
              onClick={() => { navigator.clipboard.writeText(listingUrl); toast.success("Link copied"); }}
              className="inline-flex items-center gap-1 px-3 py-2 border border-[#1f1f1f] text-white text-xs uppercase tracking-wider mono hover:border-[#ff4500]"
            >
              <Copy className="w-3.5 h-3.5" /> Copy
            </button>
          </div>
        </div>

        <CopyBlock
          title="Caption · long (Instagram + Facebook)"
          body={captionLong}
          testid={`promo-caption-long-${site.campsite_id}`}
        />
        <CopyBlock
          title="Caption · short (Stories / TikTok)"
          body={captionShort}
          testid={`promo-caption-short-${site.campsite_id}`}
        />
      </div>

      <div className="max-w-5xl mx-auto px-4 sm:px-6 mt-10 text-center text-[11px] mono text-[#666] uppercase tracking-wider">
        Tip · screen-record each scene at 9:16 and post as a carousel · the
        <span className="text-[#ff4500] mx-1">camp-compass.uk</span>ribbon stays
        visible on every clip.
      </div>
    </div>
  );
}
